import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Droplets,
  Package,
  DollarSign,
  Calendar,
  Target,
  BarChart3,
  PieChart as PieIcon,
  Boxes,
  FlaskConical,
} from "lucide-react";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";
import { it } from "date-fns/locale";

const COLORS = ["#C9A227", "#3B82F6", "#22C55E", "#EC4899", "#F59E0B", "#8B5CF6"];

const AdvancedAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), "yyyy-MM-dd"),
    end: format(new Date(), "yyyy-MM-dd"),
  });
  const [quickPeriod, setQuickPeriod] = useState("30");

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const handleQuickPeriod = (days) => {
    setQuickPeriod(days);
    if (days === "month") {
      setDateRange({
        start: format(startOfMonth(new Date()), "yyyy-MM-dd"),
        end: format(new Date(), "yyyy-MM-dd"),
      });
    } else if (days === "all") {
      setDateRange({
        start: "2026-03-01",
        end: format(new Date(), "yyyy-MM-dd"),
      });
    } else {
      setDateRange({
        start: format(subDays(new Date(), parseInt(days)), "yyyy-MM-dd"),
        end: format(new Date(), "yyyy-MM-dd"),
      });
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("token");
      
      const res = await fetch(
        `${API_URL}/api/analytics/advanced?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const result = await res.json();
      
      if (result.success) {
        setData(result);
      } else {
        toast.error("Errore nel caricamento analytics");
      }
    } catch (error) {
      console.error(error);
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value || 0);
  };

  // Format data for pie chart
  const formatPieData = useMemo(() => {
    if (!data?.format_breakdown) return [];
    return Object.entries(data.format_breakdown)
      .filter(([_, v]) => v.qty > 0)
      .map(([fmt, v]) => ({
        name: fmt,
        value: v.qty,
        liters: v.liters,
      }));
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#C9A227] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="advanced-analytics" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif text-gray-900">
            Analytics Avanzate
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Analisi dettagliata vendite, litri e previsioni
          </p>
        </div>
        
        {/* Period selector */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant={quickPeriod === "7" ? "default" : "outline"}
            onClick={() => handleQuickPeriod("7")}
            className={quickPeriod === "7" ? "bg-[#C9A227]" : ""}
          >
            7 giorni
          </Button>
          <Button
            size="sm"
            variant={quickPeriod === "30" ? "default" : "outline"}
            onClick={() => handleQuickPeriod("30")}
            className={quickPeriod === "30" ? "bg-[#C9A227]" : ""}
          >
            30 giorni
          </Button>
          <Button
            size="sm"
            variant={quickPeriod === "month" ? "default" : "outline"}
            onClick={() => handleQuickPeriod("month")}
            className={quickPeriod === "month" ? "bg-[#C9A227]" : ""}
          >
            Mese
          </Button>
          <Button
            size="sm"
            variant={quickPeriod === "all" ? "default" : "outline"}
            onClick={() => handleQuickPeriod("all")}
            className={quickPeriod === "all" ? "bg-[#C9A227]" : ""}
          >
            Totale
          </Button>
          
          <div className="flex items-center gap-1 ml-2">
            <Input
              type="date"
              value={dateRange.start}
              onChange={(e) => {
                setQuickPeriod("");
                setDateRange({ ...dateRange, start: e.target.value });
              }}
              className="w-36 h-8 text-sm"
            />
            <span className="text-gray-400">-</span>
            <Input
              type="date"
              value={dateRange.end}
              onChange={(e) => {
                setQuickPeriod("");
                setDateRange({ ...dateRange, end: e.target.value });
              }}
              className="w-36 h-8 text-sm"
            />
          </div>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 font-medium">FATTURATO</p>
                <p className="text-2xl font-bold text-amber-900">
                  {formatCurrency(data?.totals?.revenue)}
                </p>
                <p className="text-xs text-amber-600">
                  Media: {formatCurrency(data?.averages?.daily_revenue)}/giorno
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">LITRI VENDUTI</p>
                <p className="text-2xl font-bold text-blue-900">
                  {data?.totals?.liters?.toFixed(1)} L
                </p>
                <p className="text-xs text-blue-600">
                  Media: {data?.averages?.daily_liters?.toFixed(2)} L/giorno
                </p>
              </div>
              <Droplets className="w-10 h-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-medium">BOTTIGLIE</p>
                <p className="text-2xl font-bold text-green-900">
                  {data?.totals?.bottles?.toLocaleString()}
                </p>
                <p className="text-xs text-green-600">
                  Media: {data?.averages?.daily_bottles?.toFixed(0)}/giorno
                </p>
              </div>
              <Package className="w-10 h-10 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">ORDINI</p>
                <p className="text-2xl font-bold text-purple-900">
                  {data?.totals?.orders?.toLocaleString()}
                </p>
                <p className="text-xs text-purple-600">
                  Scontrino medio: {formatCurrency(data?.averages?.order_value)}
                </p>
              </div>
              <Boxes className="w-10 h-10 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Predictions Section */}
      <Card className="border-[#C9A227]/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="w-5 h-5 text-[#C9A227]" />
            Previsioni (basate sulla media del periodo)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {data?.predictions && Object.entries(data.predictions).map(([period, pred]) => (
              <div key={period} className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-500 mb-2">
                  Prossimi {period.replace('_days', ' giorni')}
                </p>
                <div className="space-y-1">
                  <p className="text-lg font-bold text-[#C9A227]">
                    {formatCurrency(pred.revenue)}
                  </p>
                  <p className="text-sm text-blue-600">
                    <Droplets className="w-3 h-3 inline mr-1" />
                    {pred.liters} litri
                  </p>
                  <p className="text-sm text-green-600">
                    <Package className="w-3 h-3 inline mr-1" />
                    {pred.bottles} bottiglie
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue & Liters Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#C9A227]" />
              Vendite e Litri per Giorno
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data?.chart_data || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) => format(new Date(v), "dd/MM", { locale: it })}
                  />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip
                    formatter={(value, name) => [
                      name === "revenue" ? formatCurrency(value) : `${value} L`,
                      name === "revenue" ? "Fatturato" : "Litri"
                    ]}
                    labelFormatter={(v) => format(new Date(v), "dd MMMM yyyy", { locale: it })}
                  />
                  <Legend />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="revenue"
                    name="Fatturato"
                    stroke="#C9A227"
                    fill="#C9A227"
                    fillOpacity={0.3}
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="liters"
                    name="Litri"
                    stroke="#3B82F6"
                    fill="#3B82F6"
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Format Breakdown Pie */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-[#C9A227]" />
              Formati Venduti
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] flex items-center">
              <ResponsiveContainer width="50%" height="100%">
                <PieChart>
                  <Pie
                    data={formatPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {formatPieData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, _, { payload }) => [`${v} pz (${payload.liters}L)`, payload.name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="w-1/2 space-y-2">
                {Object.entries(data?.format_breakdown || {})
                  .filter(([_, v]) => v.qty > 0)
                  .sort((a, b) => b[1].qty - a[1].qty)
                  .map(([fmt, v], i) => (
                    <div key={fmt} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[i % COLORS.length] }}
                        />
                        <span className="font-medium">{fmt}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold">{v.qty} pz</p>
                        <p className="text-xs text-gray-500">{v.liters} L</p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Perfumes Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#C9A227]" />
            Top 20 Profumi più Venduti
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Codice</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead className="text-right">Quantità</TableHead>
                  <TableHead className="text-right">Fatturato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.top_perfumes?.map((p, i) => (
                  <TableRow key={p.code}>
                    <TableCell className="font-bold text-[#C9A227]">{i + 1}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">{p.code}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">{p.name || '-'}</TableCell>
                    <TableCell className="text-right font-medium">{p.qty} pz</TableCell>
                    <TableCell className="text-right font-medium text-green-600">
                      {formatCurrency(p.revenue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdvancedAnalytics;
